/**
 * Frame21 – Track death certificate orders (IAD)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, Trash2, Truck } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';
import { useSettlementFrameData } from '../../hooks/useSettlementFrameData';
import { DEATH_CERT_FRAME_DATA, deathCertRoute } from '../../utils/deathCertWorkflow';

type OrderStatus = 'requested' | 'processing' | 'shipped' | 'ready_pickup';

interface OrderLine {
  id: string;
  label: string;
  quantity: number;
  status: OrderStatus;
  trackingOrRef: string;
}

function newId() {
  return `ord-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function initialOrders(): OrderLine[] {
  return [
    {
      id: newId(),
      label: 'Primary certificate order',
      quantity: 10,
      status: 'requested',
      trackingOrRef: '',
    },
  ];
}

export default function Frame21() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId?: string }>();
  const { data, save, loading } = useSettlementFrameData(estateId ?? null, DEATH_CERT_FRAME_DATA.orders);

  const [lines, setLines] = useState<OrderLine[]>(initialOrders);

  useEffect(() => {
    const raw = data.orders;
    if (!Array.isArray(raw)) return;
    setLines(raw.length ? (raw as OrderLine[]) : []);
  }, [data.orders]);

  const persist = async (next: OrderLine[]) => {
    setLines(next);
    await save({ orders: next });
  };

  const addLine = () => {
    void persist([
      ...lines,
      { id: newId(), label: '', quantity: 1, status: 'requested', trackingOrRef: '' },
    ]);
  };

  const removeLine = (id: string) => {
    void persist(lines.filter((l) => l.id !== id));
  };

  const updateLine = (id: string, patch: Partial<OrderLine>) => {
    void persist(lines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const statusOptions: { value: OrderStatus; label: string }[] = useMemo(
    () => [
      { value: 'requested', label: 'Requested' },
      { value: 'processing', label: 'Processing' },
      { value: 'shipped', label: 'Shipped / mailed' },
      { value: 'ready_pickup', label: 'Ready for pickup' },
    ],
    []
  );

  return (
    <SettlementLayout currentPhase="iad" currentStep="death-certificates" estateId={estateId ?? null}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start gap-3">
          <Truck className="w-8 h-8 text-cyan-700 shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Certificate Orders</h1>
            <p className="text-gray-600">
              Log each batch or source you ordered from, watch status, and capture tracking or reference numbers.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-700">
                <tr>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold w-24">Qty</th>
                  <th className="px-4 py-3 font-semibold w-44">Status</th>
                  <th className="px-4 py-3 font-semibold">Tracking / reference</th>
                  <th className="px-4 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {lines.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="px-4 py-2">
                      <input
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.label}
                        onChange={(e) => updateLine(row.id, { label: e.target.value })}
                        placeholder="e.g. County vital records — expedited"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min={1}
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.quantity}
                        onChange={(e) => updateLine(row.id, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.status}
                        onChange={(e) => updateLine(row.id, { status: e.target.value as OrderStatus })}
                      >
                        {statusOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.trackingOrRef}
                        onChange={(e) => updateLine(row.id, { trackingOrRef: e.target.value })}
                        placeholder="Tracking # or order #"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-600 p-1"
                        onClick={() => removeLine(row.id)}
                        aria-label="Remove row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="w-4 h-4 mr-1" />
              Add order line
            </Button>
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate(deathCertRoute('/frame-244', estateId))} disabled={loading}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={() => navigate(deathCertRoute('/frame-22', estateId))} size="lg" disabled={loading}>
            Certificates received
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
